import {
  FavoriteMealDetailsContent,
  FavoriteMealDetailsHeader,
  FavoriteMealDetailsInfo,
  IngredientList,
  FavoriteMealDetailsBody,
  IngredientListTitle,
  CtaMenuPopover,
  CtaOptionText,
} from "./styled"
import { Fragment, useState } from "react"
import Button from "components/Buttons/Button"
import TrashIcon from "components/Icons/TrashIcon"
import PopConfirm from "components/PopConfirm"
import { useSetPageTitle } from "libs/ga"
import { BackButton } from "components/Common/BackButton"
import FullPageLoading from "components/Layouts/FullPageLoading"
import ImagePreviewer from "components/foodDetails/share/FoodImage/ImageViewer"
import FoodListItem from "components/foodItem/FoodListItem"
import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import FoodImage from "components/foodDetails/share/FoodImage"
import { FOOD_DETAILS_BODY_ID } from "config"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import {
  isAIScanMeal,
  isMyMeal,
  isOpenFoodFactMeal,
} from "model/food/utils/typeGuards/mealTypeGuards"
import OpenFoodFactDescription from "components/foodDetails/share/OpenFoodFactDescription"
import FoodDetailsMealTypeSelection from "components/mealTypeSelection/FoodDetailsMealTypeSelection"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { FlexBox } from "components/Common/FlexBox"
import EditableNutrientInformation from "components/foodDetails/share/EditableNutrientInformation"
import EditNutrientForm from "components/foodDetails/share/EditNutrientForm"
import { INutrition } from "model/food/types/base.types"
import { MealType } from "model/food/types"
import HorizontalThreeDots from "components/Icons/HorizontalThreeDots"
import { Action } from "antd-mobile/es/components/action-sheet"
import SaveIcon from "components/Icons/SaveIcon"
import { useFavoriteMealDetails } from "../hooks/favoriteMealDetailsHooks"
import useUpdateMealForm from "features/meal/hooks/logMeal/useUpdateMealForm"
import { useUpdateFavoriteMeal } from "../hooks/useUpdateFavoriteMeal"
import useMealGram from "features/meal/hooks/useMealGram"
import UpdateMealNameAndPortion from "components/mealDetails/UpdateMeal/UpdateMealNameAndPortion"
import useDeleteFavoriteMeal from "../hooks/useDeleteFavoriteMeal"
import { useNavigate } from "react-router-dom"
import PATHS from "router/paths"
import useCreateFavoriteMeal from "../hooks/useCreateFavoriteMeal"
import useLogMealFromMeal from "features/meal/hooks/logMeal/useLogMealFromMeal"
import { useBackToHome } from "features/meal/hooks"
import { useResetFavoriteMealState } from "../hooks/useFavoriteMeals"

type DisplaySection = "MEAL_FORM" | "EDIT_NUTRITION_FORM"

const actions: Action[] = [
  {
    key: "save",
    text: (
      <FlexBox $alignItems="center" $gap={8}>
        <SaveIcon /> <CtaOptionText>Save</CtaOptionText>
      </FlexBox>
    ),
  },
  {
    key: "delete",
    text: (
      <FlexBox $alignItems="center" $gap={8}>
        <TrashIcon color="#FF4921" /> <CtaOptionText>Delete</CtaOptionText>
      </FlexBox>
    ),
  },
]

const FavoriteMealDetails = () => {
  useSetPageTitle("Favorite Meal Details")

  const [displaySection, setDisplaySection] =
    useState<DisplaySection>("MEAL_FORM")

  const { favoriteMealDetails, getFavoriteMealDetailsLoading } =
    useFavoriteMealDetails()
  const {
    meal,
    onPortionChange,
    onUnitChange,
    onMealTypeChange,
    updateMealAsManualNutrition,
  } = useUpdateMealForm({
    initMeal: favoriteMealDetails,
    mealType: undefined,
  })

  const [isPortionValid, setIsPortionValid] = useState(true)

  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const navigate = useNavigate()

  const backToFavoriteMeals = () => {
    navigate(PATHS.app.myFoods.root)
  }

  const updateMealSuccessCallback =
    displaySection === "EDIT_NUTRITION_FORM"
      ? () => setDisplaySection("MEAL_FORM")
      : backToFavoriteMeals

  const { handleCreateFavoriteMeal, createFavoriteMealLoading } =
    useCreateFavoriteMeal(backToFavoriteMeals)

  const { handleUpdateFavoriteMeal, updateFavoriteMealLoading } =
    useUpdateFavoriteMeal(updateMealSuccessCallback)

  const { handleDeleteFavoriteMeal, deleteFavoriteMealLoading } =
    useDeleteFavoriteMeal(backToFavoriteMeals)

  const backToHome = useBackToHome()
  const resetFavoriteMealState = useResetFavoriteMealState()

  const logMealSuccessCallback = () => {
    backToHome()
    resetFavoriteMealState()
  }

  const { handleLogMealFromMeal, logMealLoading } = useLogMealFromMeal(
    logMealSuccessCallback,
  )

  const onSubmitLogMeal = () => {
    if (meal) {
      handleLogMealFromMeal(meal)
    }
  }

  const previewImageUrl =
    favoriteMealDetails?.previewImageUrl ?? favoriteMealDetails?.imageUrl
  const [imagePreviewerVisible, setImagePreviewerVisible] = useState(false)

  const [isPopConfirmDeleteOpen, setIsPopConfirmDeleteOpen] = useState(false)
  const [isPopConfirmSaveOpen, setIsPopConfirmSaveOpen] = useState(false)

  const gram = useMealGram(meal)

  const hasIngredients = meal && (isMyMeal(meal) || isAIScanMeal(meal))

  const handleBack = () => {
    backToFavoriteMeals()
  }

  const handleSubmitUpdateNutrition = ({ gram, ...rest }: INutrition) => {
    if (meal) {
      const updatedMeal: MealType = {
        ...meal,
        ...rest,
        isManualNutrition: true,
      }

      updateMealAsManualNutrition(updatedMeal)
    }
  }

  const handleSubmitUpdateFavoriteMeal = () => {
    if (meal) {
      handleUpdateFavoriteMeal({
        ...meal,
        isManualNutrition: meal.isManualNutrition ?? false,
      })
    }
  }

  const handleSubmitCreateFavoriteMeal = () => {
    if (meal) {
      handleCreateFavoriteMeal({
        ...meal,
        isManualNutrition: meal.isManualNutrition ?? false,
      })
    }
  }

  if (displaySection === "EDIT_NUTRITION_FORM" && meal) {
    return (
      <EditNutrientForm
        onSubmit={handleSubmitUpdateNutrition}
        meal={meal}
        onClose={() => {
          setDisplaySection("MEAL_FORM")
        }}
      />
    )
  }

  return (
    <Fragment>
      {getFavoriteMealDetailsLoading || !meal ? (
        <FullPageLoading />
      ) : (
        <Fragment>
          <ImagePreviewer
            isOpen={imagePreviewerVisible}
            onClose={() => {
              setImagePreviewerVisible(false)
            }}
            imageUrl={previewImageUrl}
          />
          <FavoriteMealDetailsContent>
            <FavoriteMealDetailsBody id={FOOD_DETAILS_BODY_ID}>
              <FoodImage previewImageUrl={meal.previewImageUrl}>
                <TopNavWrapper>
                  <FavoriteMealDetailsHeader>
                    <BackButton onClick={handleBack}>
                      <ChevronLeftBtn />
                    </BackButton>

                    <FoodDetailsMealTypeSelection
                      value={meal.type}
                      onChange={(val) => onMealTypeChange(val)}
                    />
                    <div style={{ width: 24, height: 24 }}></div>
                  </FavoriteMealDetailsHeader>
                </TopNavWrapper>
              </FoodImage>

              <FavoriteMealDetailsInfo>
                <UpdateMealNameAndPortion
                  meal={meal}
                  gram={gram}
                  handlePortionChange={handlePortionChange}
                  onUnitChange={onUnitChange}
                  disabled={getFavoriteMealDetailsLoading}
                  setIsPortionValid={setIsPortionValid}
                  isFutureScheduleMeal={false}
                />

                <EditableNutrientInformation
                  entry={meal}
                  setEditMode={() => setDisplaySection("EDIT_NUTRITION_FORM")}
                />

                {hasIngredients ? (
                  <IngredientList>
                    <IngredientListTitle>Ingredients</IngredientListTitle>{" "}
                    {meal.ingredients.map((ingredient, index) => (
                      <div key={index}>
                        <FoodListItem
                          isIngredient
                          food={ingredient}
                          hideAddBtn
                          style={{ padding: "6px 0px" }}
                          isManualNutrition={meal.isManualNutrition}
                        />
                        {index !== (meal.ingredients?.length ?? 1) - 1 && (
                          <FoodListDivider />
                        )}
                      </div>
                    ))}
                  </IngredientList>
                ) : undefined}

                {isOpenFoodFactMeal(meal) && <OpenFoodFactDescription />}
              </FavoriteMealDetailsInfo>
            </FavoriteMealDetailsBody>

            <AppStyleCtaBox>
              <CtaMenuPopover
                actions={actions}
                placement="top-start"
                onAction={({ key }) => {
                  if (key === "save") {
                    setIsPopConfirmSaveOpen(true)
                  }

                  if (key === "delete") {
                    setIsPopConfirmDeleteOpen(true)
                  }
                }}
                trigger="click"
              >
                <Button
                  style={{
                    padding: "0 15px",
                    height: 48,
                    borderColor: "rgba(0, 38, 58, 0.14)",
                  }}
                  $isOption={true}
                  $size="small"
                  $type="default"
                >
                  <HorizontalThreeDots />
                </Button>
              </CtaMenuPopover>

              <Button
                loading={logMealLoading}
                $type="primary"
                style={{ width: "100%" }}
                disabled={!isPortionValid}
                onClick={onSubmitLogMeal}
              >
                Log this item
              </Button>
            </AppStyleCtaBox>
          </FavoriteMealDetailsContent>
          <PopConfirm
            isOpen={isPopConfirmDeleteOpen}
            title="Delete Favorite Food?"
            onCancel={() => setIsPopConfirmDeleteOpen(false)}
            onConfirm={() => {
              handleDeleteFavoriteMeal(meal.id)
            }}
            type="danger"
            loading={deleteFavoriteMealLoading}
          >
            <FlexBox
              $alignItems="center"
              $direction="column"
              style={{ width: "100%" }}
            >
              <div>Are you sure you want to delete this food</div>{" "}
              <div>from Favorites?</div>
            </FlexBox>
          </PopConfirm>

          <PopConfirm
            isOpen={isPopConfirmSaveOpen}
            title="Save change"
            cancelText="Save as new"
            confirmText="Save"
            isSecondaryCancel
            onCancel={handleSubmitCreateFavoriteMeal}
            onConfirm={handleSubmitUpdateFavoriteMeal}
            loading={updateFavoriteMealLoading}
            isCancelButtonLoading={createFavoriteMealLoading}
            disabled={createFavoriteMealLoading}
          >
            <FlexBox
              $alignItems="center"
              $direction="column"
              style={{ width: "100%" }}
            >
              <div>You can update your favorite food</div>{" "}
              <div>or save it as a new one.</div>
            </FlexBox>
          </PopConfirm>
        </Fragment>
      )}
    </Fragment>
  )
}

export default FavoriteMealDetails
